import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  "Not sure yet",
  "Preventative Wellness Plan",
  "Tariff Refund Program",
  "R&D Tax Credits",
  "SmartEdge Power Plan",
];

const initial = { name: "", email: "", phone: "", interest: INTEREST_OPTIONS[0], message: "" };

export default function ContactForm({ defaultInterest }) {
  const [form, setForm] = useState({
    ...initial,
    interest: defaultInterest || initial.interest,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        interest: form.interest,
      });
      setSubmitted(true);
      toast.success("Thanks — we'll be in touch within one business day.");
      setForm({ ...initial, interest: form.interest });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email info@smartedgebizsolutions.com.";
      setError(String(detail));
      toast.error("Could not send your message.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid={CONTACT.success}
        className="se-card"
        style={{ padding: 40, textAlign: "left" }}
      >
        <div className="se-badge mb-4">Message received</div>
        <h3 className="type-subheading mb-3">Thanks — we&apos;ve got it.</h3>
        <p className="type-body">
          A member of the SmartEdge team will reach out within one business day
          to schedule your free consultation.
        </p>
        <button
          type="button"
          className="btn-ghost mt-6"
          onClick={() => setSubmitted(false)}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={CONTACT.form}
      className="se-card"
      style={{ padding: 40 }}
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="se-label" htmlFor="ct-name">Full name</label>
          <input
            id="ct-name"
            name="name"
            type="text"
            required
            className="se-input"
            data-testid={CONTACT.inputName}
            value={form.name}
            onChange={onChange}
            placeholder="Jane Doe"
            autoComplete="name"
          />
        </div>
        <div>
          <label className="se-label" htmlFor="ct-email">Work email</label>
          <input
            id="ct-email"
            name="email"
            type="email"
            required
            className="se-input"
            data-testid={CONTACT.inputEmail}
            value={form.email}
            onChange={onChange}
            placeholder="jane@company.com"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="se-label" htmlFor="ct-phone">Phone</label>
          <input
            id="ct-phone"
            name="phone"
            type="tel"
            required
            className="se-input"
            data-testid={CONTACT.inputPhone}
            value={form.phone}
            onChange={onChange}
            placeholder="(555) 123-4567"
            autoComplete="tel"
          />
        </div>
        <div>
          <label className="se-label" htmlFor="ct-interest">I'm interested in</label>
          <select
            id="ct-interest"
            name="interest"
            className="se-input"
            data-testid={CONTACT.selectInterest}
            value={form.interest}
            onChange={onChange}
          >
            {INTEREST_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-5">
        <label className="se-label" htmlFor="ct-message">Tell us about your business</label>
        <textarea
          id="ct-message"
          name="message"
          required
          rows={5}
          className="se-input"
          data-testid={CONTACT.inputMessage}
          value={form.message}
          onChange={onChange}
          placeholder="Team size, industry, and what you&apos;d like to explore…"
          style={{ resize: "vertical", minHeight: 120 }}
        />
      </div>

      {error && (
        <div
          data-testid={CONTACT.error}
          className="mt-4 type-caption"
          style={{ color: "#b42318" }}
        >
          {error}
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
        <p className="type-caption" style={{ maxWidth: 360 }}>
          By submitting, you agree to be contacted by SmartEdge about your
          inquiry. No spam. No obligation.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary"
          data-testid={CONTACT.submit}
        >
          {submitting ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>
  );
}
